<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import socialCommentAutomationsAPI from 'dashboard/api/socialCommentAutomations';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  eligibleInboxes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const dialogRef = ref(null);
const isSaving = ref(false);
const nameError = ref('');
const inboxError = ref('');
const keywordsError = ref('');

const blankForm = () => ({
  id: null,
  name: '',
  inbox_id: '',
  trigger: 'comment',
  keywords: [],
  negative_keywords: [],
  // Variações (anti-repetição / A-B): sempre pelo menos 1 campo.
  public_replies: [''],
  dm_openers: [''],
  ai_context: '',
  daily_cap: 0,
  // Vazio = a regra vale para TODAS as publicações (comportamento de sempre).
  media_ids: [],
  enabled: true,
});

const form = ref(blankForm());

// Variações: seed com o array salvo ou, se vazio, com o campo único legado.
const seedVariations = (list, legacy) => {
  const arr = (Array(list) ? list : []).filter(Boolean);
  if (arr.length) return [...arr];
  return [legacy || ''];
};

const addVariation = key => {
  form.value[key] = [...form.value[key], ''];
};
const removeVariation = (key, index) => {
  form.value[key] = form.value[key].filter((_, i) => i !== index);
  if (!form.value[key].length) form.value[key] = [''];
};
const updateVariation = (key, index, value) => {
  form.value[key] = form.value[key].map((item, i) =>
    i === index ? value : item
  );
};

const inboxOptions = computed(() =>
  props.eligibleInboxes.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

const triggerOptions = computed(() => [
  {
    value: 'comment',
    label: t(
      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.COMMENT'
    ),
  },
  {
    value: 'story_reply',
    label: t(
      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.STORY_REPLY'
    ),
  },
  {
    value: 'story_mention',
    label: t(
      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.STORY_MENTION'
    ),
  },
  {
    value: 'referral',
    label: t(
      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.REFERRAL'
    ),
  },
  {
    value: 'follow',
    label: t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.FOLLOW'),
  },
]);

// Só o gatilho de comentário casa por palavra-chave; os de boas-vindas
// (story_reply/referral/follow) disparam na própria ação, sem texto pra casar.
const isCommentTrigger = computed(() => form.value.trigger === 'comment');

// Publicações da conta escolhida, para prender a regra a posts específicos.
const mediaItems = ref([]);
const isLoadingMedia = ref(false);

const loadMedia = async () => {
  mediaItems.value = [];
  if (!form.value.inbox_id || !isCommentTrigger.value) return;
  isLoadingMedia.value = true;
  try {
    const { data } = await socialCommentAutomationsAPI.media(
      form.value.inbox_id
    );
    mediaItems.value = data.media || [];
  } catch {
    mediaItems.value = [];
  } finally {
    isLoadingMedia.value = false;
  }
};

const isMediaPicked = id => form.value.media_ids.includes(String(id));

const toggleMedia = id => {
  const key = String(id);
  form.value.media_ids = isMediaPicked(key)
    ? form.value.media_ids.filter(x => x !== key)
    : [...form.value.media_ids, key];
};

watch(
  () => [form.value.inbox_id, form.value.trigger],
  () => loadMedia()
);

const selectedTriggerHint = computed(() => {
  const key = form.value.trigger.toUpperCase();
  return t(
    `INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.${key}_HINT`
  );
});

// DM opener and exclusion-keyword hints mention "the comment"/"the commenter",
// which is wrong for welcome triggers (story_reply/referral/follow): there is
// no comment, only the person who replied to a story, clicked the link, or
// followed. Swap in the welcome-flavoured sibling key for those triggers.
const dmOpenerHint = computed(() =>
  t(
    isCommentTrigger.value
      ? 'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DM_OPENER_HINT'
      : 'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DM_OPENER_WELCOME_HINT'
  )
);

const negativeKeywordsHint = computed(() =>
  t(
    isCommentTrigger.value
      ? 'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NEGATIVE_KEYWORDS_HINT'
      : 'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NEGATIVE_KEYWORDS_WELCOME_HINT'
  )
);

const isFormValid = computed(
  () =>
    Boolean(form.value.name.trim()) &&
    Boolean(form.value.inbox_id) &&
    (!isCommentTrigger.value || form.value.keywords.length > 0)
);

function open(rule = null) {
  nameError.value = '';
  inboxError.value = '';
  keywordsError.value = '';
  form.value = rule
    ? {
        id: rule.id,
        name: rule.name || '',
        inbox_id: rule.inbox_id || '',
        trigger: rule.trigger || 'comment',
        media_ids: (rule.media_ids || []).map(String),
        keywords: [...(rule.keywords || [])],
        negative_keywords: [...(rule.negative_keywords || [])],
        public_replies: seedVariations(rule.public_replies, rule.public_reply),
        dm_openers: seedVariations(rule.dm_openers, rule.dm_opener),
        ai_context: rule.ai_context || '',
        daily_cap: rule.daily_cap || 0,
        enabled: rule.enabled,
      }
    : blankForm();
  dialogRef.value?.open();
}

function close() {
  dialogRef.value?.close();
}

function buildPayload() {
  const publicReplies = form.value.public_replies
    .map(v => v.trim())
    .filter(Boolean);
  const dmOpeners = form.value.dm_openers.map(v => v.trim()).filter(Boolean);
  return {
    name: form.value.name.trim(),
    inbox_id: form.value.inbox_id,
    trigger: form.value.trigger,
    keywords: form.value.keywords,
    negative_keywords: form.value.negative_keywords,
    public_replies: publicReplies,
    dm_openers: dmOpeners,
    // Campo único legado = 1ª variação (fallback do backend).
    public_reply: publicReplies[0] || '',
    dm_opener: dmOpeners[0] || '',
    ai_context: form.value.ai_context,
    daily_cap: Number(form.value.daily_cap) || 0,
    media_ids: isCommentTrigger.value ? form.value.media_ids : [],
    enabled: form.value.enabled,
  };
}

async function handleConfirm() {
  nameError.value = form.value.name.trim()
    ? ''
    : t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NAME_REQUIRED');
  inboxError.value = form.value.inbox_id
    ? ''
    : t(
        'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.INBOX_REQUIRED'
      );
  keywordsError.value =
    !isCommentTrigger.value || form.value.keywords.length
      ? ''
      : t(
          'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.KEYWORDS_REQUIRED'
        );

  if (!isFormValid.value) {
    return;
  }

  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (form.value.id) {
      await socialCommentAutomationsAPI.update(form.value.id, payload);
      useAlert(
        t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.UPDATE_SUCCESS')
      );
    } else {
      await socialCommentAutomationsAPI.create(payload);
      useAlert(
        t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.CREATE_SUCCESS')
      );
    }
    emit('saved');
    close();
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.SAVE_ERROR');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
}

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      form.id
        ? t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.EDIT_TITLE')
        : t(
            'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.CREATE_TITLE'
          )
    "
    :confirm-button-label="
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.SAVE')
    "
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    width="lg"
    overflow-y-auto
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="
          t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NAME')
        "
        :placeholder="
          t(
            'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NAME_PLACEHOLDER'
          )
        "
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
        autofocus
      />

      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.INBOX')
          }}
        </span>
        <ComboBox
          v-model="form.inbox_id"
          :options="inboxOptions"
          :placeholder="
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.INBOX_PLACEHOLDER'
            )
          "
        />
        <span v-if="inboxError" class="text-xs text-n-ruby-11">
          {{ inboxError }}
        </span>
        <span v-if="!inboxOptions.length" class="text-xs text-n-amber-11">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.NO_ELIGIBLE_INBOXES'
            )
          }}
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGER_LABEL')
          }}
        </span>
        <ComboBox v-model="form.trigger" :options="triggerOptions" />
        <p class="text-xs text-n-slate-11">{{ selectedTriggerHint }}</p>
      </div>

      <div v-if="isCommentTrigger" class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.KEYWORDS')
          }}
        </span>
        <div
          class="flex flex-wrap w-full gap-2 px-3 py-2 border rounded-lg bg-n-alpha-black2 border-n-weak"
        >
          <TagInput
            v-model="form.keywords"
            :placeholder="
              t(
                'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.KEYWORDS_PLACEHOLDER'
              )
            "
          />
        </div>
        <span
          class="text-xs"
          :class="keywordsError ? 'text-n-ruby-11' : 'text-n-slate-11'"
        >
          {{
            keywordsError ||
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.KEYWORDS_HINT'
            )
          }}
        </span>
      </div>
      <!-- Publicações: vazio = todas. Sem isto a regra dispara em qualquer post
           da conta que tenha a palavra-chave, inclusive um post sem relação. -->
      <div v-if="isCommentTrigger" class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.MEDIA')
          }}
        </span>
        <p v-if="isLoadingMedia" class="text-xs text-n-slate-11">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.MEDIA_LOADING'
            )
          }}
        </p>
        <p v-else-if="!mediaItems.length" class="text-xs text-n-slate-11">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.MEDIA_EMPTY'
            )
          }}
        </p>
        <div v-else class="grid grid-cols-4 gap-2">
          <button
            v-for="item in mediaItems"
            :key="item.id"
            type="button"
            :data-testid="`media-${item.id}`"
            class="relative overflow-hidden rounded-lg border"
            :class="
              isMediaPicked(item.id)
                ? 'border-n-brand ring-2 ring-n-brand/40'
                : 'border-n-weak'
            "
            :title="item.caption"
            @click="toggleMedia(item.id)"
          >
            <img
              v-if="item.thumbnail"
              :src="item.thumbnail"
              class="h-16 w-full object-cover"
              alt=""
            />
            <span
              v-else
              class="flex h-16 items-center justify-center bg-n-alpha-2 text-n-slate-10"
            >
              <span class="i-lucide-image" />
            </span>
          </button>
        </div>
        <span class="text-xs text-n-slate-11">
          {{
            form.media_ids.length
              ? t(
                  'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.MEDIA_PICKED',
                  { count: form.media_ids.length }
                )
              : t(
                  'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.MEDIA_ALL'
                )
          }}
        </span>
      </div>

      <p v-else class="text-xs text-n-slate-11">
        {{
          t(
            'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.KEYWORDS_ONLY_ON_COMMENT'
          )
        }}
      </p>

      <!-- Palavras de EXCLUSÃO (opt-out): se o comentário tiver alguma, não dispara -->
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NEGATIVE_KEYWORDS'
            )
          }}
        </span>
        <div
          class="flex flex-wrap w-full gap-2 px-3 py-2 border rounded-lg bg-n-alpha-black2 border-n-weak"
        >
          <TagInput
            v-model="form.negative_keywords"
            :placeholder="
              t(
                'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.NEGATIVE_KEYWORDS_PLACEHOLDER'
              )
            "
          />
        </div>
        <span class="text-xs text-n-slate-11">
          {{ negativeKeywordsHint }}
        </span>
      </div>

      <!-- Variações da resposta pública: só existe comentário pra responder no gatilho "comment" -->
      <div v-if="isCommentTrigger" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.PUBLIC_REPLY'
            )
          }}
        </span>
        <div
          v-for="(variation, index) in form.public_replies"
          :key="index"
          class="flex items-start gap-2"
        >
          <TextArea
            class="flex-1"
            :model-value="variation"
            :placeholder="
              t(
                'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.PUBLIC_REPLY_PLACEHOLDER'
              )
            "
            @update:model-value="
              value => updateVariation('public_replies', index, value)
            "
          />
          <button
            v-if="form.public_replies.length > 1"
            type="button"
            class="mt-2 shrink-0 text-n-slate-11 hover:text-n-ruby-11"
            @click="removeVariation('public_replies', index)"
          >
            <span class="i-lucide-x" />
          </button>
        </div>
        <button
          type="button"
          class="self-start text-xs font-medium text-n-brand hover:underline"
          @click="addVariation('public_replies')"
        >
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.ADD_VARIATION'
            )
          }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.PUBLIC_REPLY_HINT'
            )
          }}
        </span>
      </div>

      <!-- Variações da abertura de DM -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DM_OPENER'
            )
          }}
        </span>
        <div
          v-for="(variation, index) in form.dm_openers"
          :key="index"
          class="flex items-start gap-2"
        >
          <TextArea
            class="flex-1"
            :model-value="variation"
            :placeholder="
              t(
                'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DM_OPENER_PLACEHOLDER'
              )
            "
            @update:model-value="
              value => updateVariation('dm_openers', index, value)
            "
          />
          <button
            v-if="form.dm_openers.length > 1"
            type="button"
            class="mt-2 shrink-0 text-n-slate-11 hover:text-n-ruby-11"
            @click="removeVariation('dm_openers', index)"
          >
            <span class="i-lucide-x" />
          </button>
        </div>
        <button
          type="button"
          class="self-start text-xs font-medium text-n-brand hover:underline"
          @click="addVariation('dm_openers')"
        >
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.ADD_VARIATION'
            )
          }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{ dmOpenerHint }}
        </span>
      </div>

      <!-- Contexto/oferta pra IA nutrir -->
      <TextArea
        v-model="form.ai_context"
        :label="
          t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.AI_CONTEXT')
        "
        :placeholder="
          t(
            'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.AI_CONTEXT_PLACEHOLDER'
          )
        "
        :message="
          t(
            'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.AI_CONTEXT_HINT'
          )
        "
      />

      <!-- Teto diário de DMs (anti-ban) -->
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DAILY_CAP'
            )
          }}
        </span>
        <input
          v-model.number="form.daily_cap"
          type="number"
          min="0"
          class="no-margin block w-32 box-border px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand h-10"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.DAILY_CAP_HINT'
            )
          }}
        </span>
      </label>

      <label class="flex items-center gap-2">
        <Switch v-model="form.enabled" />
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DIALOG.ENABLED')
          }}
        </span>
      </label>
    </div>
  </Dialog>
</template>
